import { Texture } from 'pixi.js';
import type { RoomKind } from '../ui/homeModel.js';
import type { LivePart } from '../ui/rooms.js';
import { partDocument, roomDocument } from '../ui/rooms.js';

/**
 * Rooms as textures: the home screen's SVG drawings, rasterised once each.
 *
 * Drawn at three times their 240×96 size, so a tall floor on a dense phone
 * still gets a sharp picture; cached for the whole session, since a room looks
 * the same on every level that uses it. A room's moving parts are left out of
 * its picture and rasterised on their own, to be moved over it.
 */
export const SCALE = 3;
const rooms = new Map<RoomKind, Promise<Texture>>();
const parts = new Map<LivePart, Promise<Texture>>();

async function rasterise(svg: string, width: number, height: number): Promise<Texture> {
  const image = new Image();
  image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  await image.decode();
  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(width * SCALE);
  canvas.height = Math.ceil(height * SCALE);
  canvas.getContext('2d')?.drawImage(image, 0, 0, canvas.width, canvas.height);
  return Texture.from(canvas);
}

export function roomTexture(kind: RoomKind): Promise<Texture> {
  const cached = rooms.get(kind);
  if (cached) return cached;
  const loading = rasterise(roomDocument(kind, { rugs: false, live: false }), 240, 96);
  rooms.set(kind, loading);
  return loading;
}

export function partTexture(part: LivePart): Promise<Texture> {
  const cached = parts.get(part);
  if (cached) return cached;
  const loading = rasterise(partDocument(part), part.box.width, part.box.height);
  parts.set(part, loading);
  return loading;
}
